import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useUnit } from '../contexts/UnitContext';
import { Button } from './ui/button';
import { LogOut, Bell, Building2, ArrowUpRight } from 'lucide-react';

/**
 * Floating command bar.
 *
 * Sits on the atmospheric page rather than against it: a rounded glass strip
 * with alerts, the active unit, and the signed-in account.
 * Every icon-only control carries an aria-label.
 */
export default function Header() {
  const { user, logout } = useAuth();
  const { activeUnit, canSwitch, setActiveUnit } = useUnit();
  const navigate = useNavigate();
  const initials = (user?.full_name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const allUnits = activeUnit === 'All Units';

  return (
    <header data-testid="header" className="enter-down sticky top-0 z-30 bg-[#F7FAFF] px-4 sm:px-6 pt-5 pb-3">
      <div className="flex items-center justify-between gap-3">

        {/* The global search box that used to sit here was removed: it had no
            handler at all, on any page — it looked like a feature and did
            nothing. Each screen has its own working search instead (employees,
            allocation, templates, KPI master). */}

        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1 justify-end">

          {/* -------- Alerts -------- */}
          <button data-testid="notifications" aria-label="Notifications"
                  className="relative grid place-items-center w-10 h-10 rounded-full bg-white text-[#6B7280]
                             shadow-[0_2px_10px_rgba(15,23,42,0.06)]
                             transition-colors duration-150 hover:text-[#2563EB]">
            <Bell className="w-[18px] h-[18px]" strokeWidth={1.9} />
            <span aria-hidden="true"
                  className="absolute -top-0.5 -right-0.5 grid place-items-center min-w-[17px] h-[17px] px-[4px]
                             rounded-full text-[10px] font-bold text-white ring-2 ring-[#F7FAFF]
                             bg-[#EF4444]">1</span>
          </button>

          <div aria-hidden="true" className="w-px h-7 bg-[#E5E7EF] hidden sm:block" />

          {/* -------- Active unit -------- */}
          {activeUnit && (
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              <span
                className={`inline-flex items-center gap-2 text-[13px] font-semibold px-4 py-2.5 rounded-full
                            border transition-all duration-200 hover:-translate-y-px truncate
                            max-w-[22vw] xs:max-w-[34vw] sm:max-w-none whitespace-nowrap
                            ${allUnits
                              ? 'text-white border-transparent shadow-[0_6px_18px_-8px_rgba(37,99,235,0.9)]'
                              : 'text-[#2563EB] border-transparent bg-[#EFF6FF] hover:bg-[#DBEAFE]'}`}
                style={allUnits ? { background: 'var(--g-primary)' } : undefined}>
                <Building2 className="w-4 h-4 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="truncate">{allUnits ? 'All Units — Corporate HQ' : activeUnit}</span>
              </span>
              {canSwitch && (
                <button data-testid="switch-unit"
                        onClick={() => { setActiveUnit(null); navigate('/select-unit'); }}
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2563EB]
                                   flex-shrink-0 px-4 py-2.5 rounded-full bg-[#EFF6FF]
                                   transition-colors duration-150 hover:bg-[#DBEAFE]">
                  Switch
                  <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.4} aria-hidden="true" />
                </button>
              )}
              <div aria-hidden="true" className="w-px h-7 bg-[#E5E7EF] hidden sm:block" />
            </div>
          )}

          {/* -------- Account -------- */}
          <div className="text-right hidden sm:block">
            <p className="text-[14px] font-bold text-[#0F172A] leading-tight">{user?.full_name}</p>
            <p className="text-[11.5px] font-medium text-[#6B7280] capitalize">{user?.role}</p>
          </div>

          <div className="grid place-items-center w-10 h-10 rounded-full flex-shrink-0
                          text-white text-[13px] font-bold"
               style={{ background: 'var(--g-primary)' }}
               aria-hidden="true">
            {initials}
          </div>

          <Button data-testid="logout-button" onClick={logout} variant="outline" size="icon"
                  aria-label="Log out"
                  className="flex-shrink-0 rounded-full border-transparent bg-white shadow-[0_2px_10px_rgba(15,23,42,0.06)] text-[#6B7280] hover:text-[#EF4444] hover:bg-[#FEF2F2]">
            <LogOut className="w-4 h-4" strokeWidth={1.9} />
          </Button>
        </div>
      </div>
    </header>
  );
}
